import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  ArrowForward as ArrowForwardIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo';

const services = [
  {
    icon: <VerifiedIcon />,
    number: '01',
    title: 'Quality tire selection',
    description: 'Everyday and performance options selected for dependable grip, comfort, and value.',
  },
  {
    icon: <BuildIcon />,
    number: '02',
    title: 'Professional fitting',
    description: 'Careful tire mounting and balancing carried out by an experienced local team.',
  },
  {
    icon: <SpeedIcon />,
    number: '03',
    title: 'Fast local service',
    description: 'Clear recommendations and efficient service so you can get safely back on the road.',
  },
  {
    icon: <LocationIcon />,
    number: '04',
    title: 'Based in GenSan',
    description: 'A local shop serving drivers throughout General Santos City and nearby communities.',
  },
];

const steps = [
  ['Search the catalog', 'Check available tire, oil, rim, and service products before visiting.'],
  ['Choose the right fit', 'Compare brands and sizes, then confirm availability with the shop.'],
  ['Visit for installation', 'Have your tires professionally fitted and balanced by the AzTires team.'],
];

const PublicHomepage = () => {
  const navigate = useNavigate();

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0B0D0F', color: '#F4F6F8' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: 'rgba(11,13,15,0.96)', borderBottom: '1px solid #20242A', backdropFilter: 'blur(14px)' }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 78 }, justifyContent: 'space-between', gap: 2 }}>
            <BrandLogo height={46} sx={{ cursor: 'pointer' }} onClick={() => navigate('/')} />

            <Stack direction="row" spacing={{ xs: 0.75, sm: 1.25 }} alignItems="center">
              <Button
                onClick={scrollToServices}
                sx={{ color: '#C1C6CC', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Services
              </Button>
              <Button variant="contained" onClick={() => navigate('/catalog')}>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Browse catalog</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Catalog</Box>
              </Button>
              <Button
                variant="outlined"
                startIcon={<AdminIcon />}
                onClick={() => navigate('/admin')}
                sx={{ minWidth: 0, px: { xs: 1.25, sm: 2 } }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Admin</Box>
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main">
        <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderBottom: '1px solid #20242A' }}>
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ maxWidth: 650 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <Box sx={{ width: 36, height: 3, bgcolor: '#E13B36' }} />
                    <Typography variant="overline" sx={{ color: '#D5D9DD', fontWeight: 800, letterSpacing: '0.16em' }}>
                      General Santos City tire service
                    </Typography>
                  </Stack>

                  <Typography
                    component="h1"
                    sx={{
                      maxWidth: 620,
                      fontWeight: 800,
                      letterSpacing: '-0.055em',
                      lineHeight: 0.98,
                      fontSize: { xs: '3rem', sm: '4rem', md: '5.25rem' },
                    }}
                  >
                    Better tires.
                    <Box component="span" sx={{ display: 'block', color: '#E13B36' }}>Better drive.</Box>
                  </Typography>

                  <Typography
                    sx={{ mt: 3, maxWidth: 570, color: '#A8AFB7', lineHeight: 1.75, fontSize: { xs: '1rem', md: '1.15rem' } }}
                  >
                    Find dependable tires, straightforward advice, and professional installation from a local team
                    that puts safety and value first.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4.5 }}>
                    <Button
                      size="large"
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/catalog')}
                      sx={{ px: 3, py: 1.35 }}
                    >
                      View available products
                    </Button>
                    <Button size="large" variant="outlined" onClick={scrollToServices} sx={{ px: 3, py: 1.35 }}>
                      See our services
                    </Button>
                  </Stack>

                  <Grid container spacing={2} sx={{ mt: 4.5, maxWidth: 600 }}>
                    {['Clear product availability', 'Professional fitting', 'Local GenSan service'].map((item) => (
                      <Grid item xs={12} sm={4} key={item}>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Box sx={{ width: 6, height: 6, bgcolor: '#E13B36', mt: '7px', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: '#C1C6CC', lineHeight: 1.45, fontWeight: 600 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  component="figure"
                  sx={{ m: 0, position: 'relative', border: '1px solid #343A40', bgcolor: '#111418', p: 1.25, borderRadius: 2 }}
                >
                  <Box
                    component="img"
                    src="/tire-service-hero.jpg"
                    alt="A professional mechanic inspecting a tire in a workshop"
                    sx={{
                      width: '100%',
                      height: { xs: 330, sm: 430, md: 540 },
                      display: 'block',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      borderRadius: 1,
                      filter: 'saturate(0.78) contrast(1.05)',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 22, sm: 30 },
                      right: { xs: 22, sm: 'auto' },
                      bottom: { xs: 22, sm: 30 },
                      width: { sm: 310 },
                      p: 2.25,
                      bgcolor: 'rgba(11,13,15,0.94)',
                      borderLeft: '4px solid #E13B36',
                    }}
                  >
                    <Typography variant="overline" sx={{ color: '#E13B36', fontWeight: 800, letterSpacing: '0.12em' }}>
                      Workshop service
                    </Typography>
                    <Typography sx={{ mt: 0.25, fontWeight: 700, fontSize: '1.05rem' }}>
                      Professional fitting and balancing for a safer drive.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Box component="section" id="services" sx={{ py: { xs: 8, md: 11 }, bgcolor: '#111418' }}>
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 3, md: 6 }} alignItems="end" sx={{ mb: { xs: 4, md: 6 } }}>
              <Grid item xs={12} md={7}>
                <Typography variant="overline" sx={{ color: '#E13B36', fontWeight: 800, letterSpacing: '0.16em' }}>
                  What we do
                </Typography>
                <Typography
                  component="h2"
                  sx={{ mt: 1, fontWeight: 800, letterSpacing: '-0.035em', fontSize: { xs: '2.1rem', md: '3.35rem' } }}
                >
                  Tire service without the noise.
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography sx={{ color: '#9AA1A9', lineHeight: 1.75 }}>
                  Straight answers, dependable products, and the practical service your vehicle actually needs.
                </Typography>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {services.map((service) => (
                <Grid item xs={12} sm={6} lg={3} key={service.number}>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: { xs: 2.75, md: 3.25 },
                      borderRadius: 1.5,
                      bgcolor: '#15181C',
                      border: '1px solid #282D33',
                      borderTop: '3px solid #E13B36',
                      transition: 'border-color 0.18s, background-color 0.18s',
                      '&:hover': { borderColor: '#414850', bgcolor: '#181C21' },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                      <Box sx={{ color: '#E13B36', display: 'flex', '& svg': { fontSize: 28 } }}>{service.icon}</Box>
                      <Typography sx={{ color: '#555D66', fontWeight: 800, fontSize: '0.8rem' }}>{service.number}</Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ fontWeight: 750, mb: 1.5 }}>{service.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#9AA1A9', lineHeight: 1.7 }}>{service.description}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <Box component="section" sx={{ py: { xs: 8, md: 11 }, borderTop: '1px solid #20242A', borderBottom: '1px solid #20242A' }}>
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 5, md: 9 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <Typography variant="overline" sx={{ color: '#E13B36', fontWeight: 800, letterSpacing: '0.16em' }}>
                  Simple process
                </Typography>
                <Typography component="h2" sx={{ mt: 1, fontWeight: 800, letterSpacing: '-0.035em', fontSize: { xs: '2.1rem', md: '3.2rem' } }}>
                  From search to fitted.
                </Typography>
                <Typography sx={{ color: '#9AA1A9', mt: 2, lineHeight: 1.75 }}>
                  Start online, confirm with the team, and finish at the shop with professional installation.
                </Typography>
                <Button variant="contained" endIcon={<SearchIcon />} onClick={() => navigate('/catalog')} sx={{ mt: 3.5 }}>
                  Search the catalog
                </Button>
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack spacing={0} sx={{ borderTop: '1px solid #282D33' }}>
                  {steps.map(([title, description], index) => (
                    <Grid
                      container
                      spacing={2}
                      key={title}
                      sx={{ py: 3, borderBottom: '1px solid #282D33', alignItems: 'flex-start' }}
                    >
                      <Grid item xs={2} sm={1.5}>
                        <Typography sx={{ color: '#E13B36', fontWeight: 800 }}>0{index + 1}</Typography>
                      </Grid>
                      <Grid item xs={10} sm={3.5}>
                        <Typography sx={{ fontWeight: 750 }}>{title}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={7}>
                        <Typography variant="body2" sx={{ color: '#9AA1A9', lineHeight: 1.7 }}>{description}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      <Box component="footer" sx={{ py: { xs: 5, md: 6 }, bgcolor: '#08090B' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <BrandLogo height={48} />
              <Typography variant="body2" sx={{ mt: 2, color: '#7F8790', maxWidth: 420, lineHeight: 1.7 }}>
                Tire sales and professional fitting in General Santos City, South Cotabato.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 4 }} justifyContent={{ md: 'flex-end' }}>
                <Button color="inherit" onClick={() => navigate('/catalog')} sx={{ color: '#A8AFB7' }}>Catalog</Button>
                <Button color="inherit" onClick={scrollToServices} sx={{ color: '#A8AFB7' }}>Services</Button>
                <Button color="inherit" onClick={() => navigate('/admin')} sx={{ color: '#A8AFB7' }}>Admin portal</Button>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #20242A', display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ color: '#69717A' }}>
              © {new Date().getFullYear()} AzTires. All rights reserved.
            </Typography>
            <Typography
              component="a"
              href="https://unsplash.com/photos/a-man-working-on-a-tire-in-a-garage-9uHal2Dd9aE"
              target="_blank"
              rel="noreferrer"
              variant="caption"
              sx={{ color: '#69717A', textDecoration: 'none', '&:hover': { color: '#A8AFB7' } }}
            >
              Workshop photo by Jimmy Nilsson Masth / Unsplash
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicHomepage;
